/**
 * 地图相关的用户偏好：底图源、是否跟随、是否只用缓存。
 *
 * 存 `localStorage` 而不是别的地方，理由与密钥一致：这是纯前端应用，没有后端。
 * 三者都**只影响显示**，所以读失败一律静默回落到默认值 —— "偏好读不出来"绝不该
 * 让地图起不来（隐私模式下 `localStorage` 会直接抛异常）。
 *
 * 为什么要持久化底图源：野外的机器往往是重新打开浏览器就继续作业，如果每次都
 * 把用户拽回 OSM，那他精心配好的（可能还需要密钥的）内网瓦片服务等于每次都要重选。
 */

import { DEFAULT_SOURCE_ID, type BaseMapSource } from './sources';

export const MAP_SOURCE_STORAGE_KEY = 'hgc.map.sourceId';
export const MAP_FOLLOW_STORAGE_KEY = 'hgc.map.follow';
export const MAP_OFFLINE_ONLY_STORAGE_KEY = 'hgc.map.offlineOnly';

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 存不下只影响下次启动的默认值，不影响本次会话 */
  }
}

/**
 * 上次选的底图源 id。
 *
 * 必须对着**当前目录**校验：源目录会变（用户删掉了自填源、或版本升级后下线了某个源），
 * 而一个指向已不存在的源的偏好会让选择器显示空白、地图请求一个没人认得的源 ——
 * 那种症状看起来像"底图服务挂了"。
 */
export function readSourcePreference(sources: readonly BaseMapSource[]): string {
  const stored = readRaw(MAP_SOURCE_STORAGE_KEY);
  if (stored !== null && sources.some((source) => source.id === stored)) return stored;
  return DEFAULT_SOURCE_ID;
}

export function writeSourcePreference(sourceId: string): void {
  writeRaw(MAP_SOURCE_STORAGE_KEY, sourceId);
}

/** 默认跟随：新用户打开就该看到机体，而不是一个空的世界视图。 */
export function readFollowPreference(fallback = true): boolean {
  const stored = readRaw(MAP_FOLLOW_STORAGE_KEY);
  if (stored === null) return fallback;
  return stored === 'true';
}

export function writeFollowPreference(follow: boolean): void {
  writeRaw(MAP_FOLLOW_STORAGE_KEY, follow ? 'true' : 'false');
}

/**
 * 是否只用已缓存地图。
 *
 * 默认 **false（允许联网）**：野外没有可靠的办法知道"我现在到底有没有网"，
 * 默认走向离线会让第一次打开的用户看到一片空白，而且他会以为是软件坏了。
 * 断网时由 `tileProtocol` 的失败路径自然退化成"用缓存 + 占位图"，不需要提前猜。
 */
export function readOfflineOnlyPreference(): boolean {
  return readRaw(MAP_OFFLINE_ONLY_STORAGE_KEY) === 'true';
}

export function writeOfflineOnlyPreference(offlineOnly: boolean): void {
  writeRaw(MAP_OFFLINE_ONLY_STORAGE_KEY, offlineOnly ? 'true' : 'false');
}
